import React, { useEffect, useState } from 'react';
import { View, Text, Modal, ScrollView, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import Button from './Button';
import Input from './Input';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

/** Registered vehicle shape returned by the subscriptions API (mig 0055). */
export interface SubscriptionVehicle {
  licensePlate: string;
  make: string;
  model: string;
  color: string;
  year: number | null;
  /** "2018 White Ford F-150" (year/color omitted when missing). */
  description: string;
  canEdit: boolean;
  nextEditAt: string | null;
}

export interface VehicleFields {
  licensePlate: string;
  make: string;
  model: string;
  color: string;
  year: number | null;
}

/** "2018 White Ford F-150, plate ABC123" — the redemption confirmation line. */
export function formatVehicleLine(v: Pick<SubscriptionVehicle, 'description' | 'licensePlate'>): string {
  return `${v.description}, plate ${v.licensePlate}`;
}

/**
 * VehicleProfileModal - create/edit the single registered vehicle on a
 * membership. Customer mode shows the anti-sharing notice and the 15-day
 * edit-lock warning; owner mode (override edits) shows neither.
 * Custom transparent Modal — multi-button Alert.alert collapses on web.
 */
interface VehicleProfileModalProps {
  readonly visible: boolean;
  readonly mode: 'customer' | 'owner';
  readonly initial?: Pick<SubscriptionVehicle, 'licensePlate' | 'make' | 'model' | 'color' | 'year'> | null;
  /** Optional context line under the title, e.g. the plan/member name. */
  readonly contextLine?: string;
  /** Performs the API call; throw to surface the error inline. */
  readonly onSubmit: (fields: VehicleFields) => Promise<void>;
  readonly onClose: () => void;
  /** When false the close/cancel affordances are hidden (profile required to proceed). */
  readonly dismissable?: boolean;
}

export default function VehicleProfileModal({
  visible, mode, initial, contextLine, onSubmit, onClose, dismissable = true,
}: VehicleProfileModalProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [plate, setPlate] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Re-seed the form each time the modal opens (initial may have loaded late).
  useEffect(() => {
    if (!visible) return;
    setPlate(initial?.licensePlate ?? '');
    setMake(initial?.make ?? '');
    setModel(initial?.model ?? '');
    setColor(initial?.color ?? '');
    setYear(initial?.year != null ? String(initial.year) : '');
    setError(null);
  }, [visible]);

  const handleSave = async () => {
    const trimmedPlate = plate.trim();
    if (!trimmedPlate || !make.trim() || !model.trim() || !color.trim()) {
      setError('Plate, make, model, and color are required.');
      return;
    }
    let yearNum: number | null = null;
    if (year.trim()) {
      yearNum = parseInt(year.trim(), 10);
      if (isNaN(yearNum) || yearNum < 1900 || yearNum > 2100) {
        setError('Enter a valid year (or leave it blank).');
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        licensePlate: trimmedPlate,
        make: make.trim(),
        model: model.trim(),
        color: color.trim(),
        year: yearNum,
      });
    } catch (err: any) {
      setError(err?.message || 'Could not save the vehicle profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={dismissable ? onClose : () => {}}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <ScrollView keyboardShouldPersistTaps="handled">
            <View style={styles.titleRow}>
              <Ionicons name="car-outline" size={22} color={colors.primary} />
              <Text style={styles.title}>{initial ? 'Update Your Vehicle' : 'Register Your Vehicle'}</Text>
            </View>
            {contextLine && <Text style={styles.contextLine}>{contextLine}</Text>}

            {mode === 'customer' && (
              <View style={styles.noticeBox}>
                <Ionicons name="shield-checkmark-outline" size={18} color={colors.primary} style={{ marginTop: 1 }} />
                <Text style={styles.noticeText}>
                  Membership is valid for one registered vehicle. Sharing may result in suspension.
                </Text>
              </View>
            )}

            <Input label="License Plate" value={plate} onChangeText={setPlate}
              placeholder="ABC123" autoCapitalize="characters" maxLength={10} />
            <Input label="Make" value={make} onChangeText={setMake} placeholder="Ford" maxLength={40} />
            <Input label="Model" value={model} onChangeText={setModel} placeholder="F-150" maxLength={40} />
            <Input label="Color" value={color} onChangeText={setColor} placeholder="White" maxLength={30} />
            <Input label="Year (optional)" value={year} onChangeText={setYear}
              placeholder="2018" keyboardType="number-pad" maxLength={4} />

            {mode === 'customer' && (
              <View style={styles.warnBox}>
                <Ionicons name="time-outline" size={18} color={colors.warning} style={{ marginTop: 1 }} />
                <Text style={styles.warnText}>
                  You can change your vehicle profile only once every 15 days, so double-check the details.
                  Mistakes can be fixed within 1 hour of saving.
                </Text>
              </View>
            )}

            {error && <Text style={styles.errorText}>{error}</Text>}

            <Button
              title={saving ? 'Saving...' : initial ? 'Save Vehicle' : 'Register Vehicle'}
              onPress={handleSave}
              loading={saving}
              size="lg"
              style={{ marginTop: spacing.md }}
            />
            {dismissable && (
              <Button title="Cancel" onPress={onClose} variant="outline" size="sm"
                style={{ marginTop: spacing.sm }} disabled={saving} />
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  card: { width: '100%', maxWidth: 420, maxHeight: '90%', backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  contextLine: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.sm },
  noticeBox: { flexDirection: 'row', gap: spacing.sm, padding: spacing.md, backgroundColor: c.primary + '10', borderRadius: borderRadius.md, marginBottom: spacing.md },
  noticeText: { flex: 1, fontSize: fontSize.sm, color: c.text, lineHeight: 20 },
  warnBox: { flexDirection: 'row', gap: spacing.sm, padding: spacing.md, backgroundColor: c.warning + '10', borderRadius: borderRadius.md, marginTop: spacing.xs },
  warnText: { flex: 1, fontSize: fontSize.xs, color: c.textSecondary, lineHeight: 18 },
  errorText: { fontSize: fontSize.sm, color: c.danger, marginTop: spacing.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
